import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const reviewSchema = z.object({
  name: z.string().trim().min(2, "Please tell us your name").max(120),
  email: z.string().trim().email("Please enter a valid email address").max(200),
  suburb: z.string().trim().max(120).optional().or(z.literal("")),
  matterType: z.string().trim().max(120).optional().or(z.literal("")),
  rating: z.number().int().min(1).max(5),
  body: z.string().trim().min(20, "Please give us a little more detail").max(3000),
  consent: z.boolean(),
  sourcePage: z.string().trim().max(200).optional().or(z.literal("")),
});

export type PublishedReview = {
  id: string;
  name: string;
  suburb: string | null;
  matterType: string | null;
  rating: number;
  body: string;
  createdAt: string;
};

/** Public list of reviews the firm has approved for publication. */
export const listPublishedReviews = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublishedReview[]> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("reviews" as never)
      .select("id, name, suburb, matter_type, rating, body, created_at")
      .eq("published", true)
      .order("created_at", { ascending: false })
      .limit(50);

    if (error) {
      console.error("Failed to load reviews", error);
      return [];
    }

    return ((data ?? []) as unknown as Record<string, string | number | null>[]).map((row) => ({
      id: String(row["id"]),
      name: String(row["name"] ?? ""),
      suburb: (row["suburb"] as string | null) ?? null,
      matterType: (row["matter_type"] as string | null) ?? null,
      rating: Number(row["rating"] ?? 5),
      createdAt: String(row["created_at"] ?? ""),
      body: String(row["body"] ?? ""),
    }));
  },
);

/** Stores a client review and emails it straight through to the firm. */
export const submitReview = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => reviewSchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { error } = await supabaseAdmin.from("reviews" as never).insert({
      name: data.name,
      email: data.email,
      suburb: data.suburb || null,
      matter_type: data.matterType || null,
      rating: data.rating,
      body: data.body,
      consent: data.consent,
      published: false,
      source_page: data.sourcePage || null,
    } as never);

    if (error) {
      console.error("Failed to store review", error);
      throw new Error("We could not send your review. Please email us instead.");
    }

    const { sendLeadToCrm } = await import("@/lib/crm.server");
    await sendLeadToCrm({
      formType: "review",
      name: data.name,
      email: data.email,
      matterType: data.matterType || null,
      sourcePage: data.sourcePage || "/reviews",
      message: data.body,
      details: {
        rating: `${data.rating} / 5`,
        suburb: data.suburb || null,
        "consent to publish": data.consent ? "Yes" : "No",
      },
    });

    return { ok: true } as const;
  });

export type ReviewStats = { count: number; average: number };

/** Aggregate rating from reviews the firm has published on the site. */
export const getReviewStats = createServerFn({ method: "GET" }).handler(
  async (): Promise<ReviewStats> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("reviews" as never)
      .select("rating")
      .eq("published", true);

    if (error || !data || data.length === 0) return { count: 0, average: 0 };
    const ratings = (data as unknown as { rating: number }[]).map((r) => Number(r.rating) || 0);
    const average = ratings.reduce((a, b) => a + b, 0) / ratings.length;
    return { count: ratings.length, average: Math.round(average * 10) / 10 };
  },
);

const moderationInput = z.object({ code: z.string().trim().min(1).max(200) });

function assertCode(code: string) {
  const expected = process.env["ANALYTICS_ACCESS_CODE"];
  if (!expected || code !== expected) throw new Error("That access code is not correct.");
}

export type ModeratedReview = PublishedReview & { email: string | null; published: boolean };

/** Firm-only: every review, newest first, so each can be approved or hidden. */
export const listAllReviews = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => moderationInput.parse(data))
  .handler(async ({ data }): Promise<ModeratedReview[]> => {
    assertCode(data.code);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin
      .from("reviews" as never)
      .select("id, name, email, suburb, matter_type, rating, body, created_at, published")
      .order("created_at", { ascending: false })
      .limit(200);

    if (error) throw new Error("Could not load reviews.");

    return ((rows ?? []) as unknown as Record<string, string | number | boolean | null>[]).map(
      (row) => ({
        id: String(row["id"]),
        name: String(row["name"] ?? ""),
        email: (row["email"] as string | null) ?? null,
        suburb: (row["suburb"] as string | null) ?? null,
        matterType: (row["matter_type"] as string | null) ?? null,
        rating: Number(row["rating"] ?? 5),
        body: String(row["body"] ?? ""),
        createdAt: String(row["created_at"] ?? ""),
        published: Boolean(row["published"]),
      }),
    );
  });

/** Firm-only: publish or hide a single review. */
export const setReviewPublished = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    moderationInput.extend({ id: z.string().uuid(), published: z.boolean() }).parse(data),
  )
  .handler(async ({ data }) => {
    assertCode(data.code);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("reviews" as never)
      .update({ published: data.published } as never)
      .eq("id", data.id);
    if (error) throw new Error("Could not update that review.");
    return { ok: true } as const;
  });
